import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            await register(name, email, password);
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div className="hidden lg:flex lg:w-1/2 login-bg items-center justify-center p-12">
                <div className="text-white max-w-md animate-fade-in">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center">
                            <Clock className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            SwissTime
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-none mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Start Tracking Today
                    </h1>
                    <p className="text-lg text-zinc-300 leading-relaxed">
                        Join thousands of teams who trust SwissTime for accurate time tracking and billing. Get started in minutes.
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-zinc-50">
                <div className="w-full max-w-md animate-fade-in">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
                            <Clock className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-extrabold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            SwissTime
                        </span>
                    </div>

                    <div className="swiss-card">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Create account
                            </h2>
                            <p className="text-zinc-500">
                                Get started with your free account
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                            <div className="form-group">
                                <Label htmlFor="name" className="form-label">Full Name</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="John Doe"
                                    required
                                    className="swiss-input"
                                    data-testid="register-name-input"
                                />
                            </div>

                            <div className="form-group">
                                <Label htmlFor="email" className="form-label">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    className="swiss-input"
                                    data-testid="register-email-input"
                                />
                            </div>

                            <div className="form-group">
                                <Label htmlFor="password" className="form-label">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    required
                                    className="swiss-input"
                                    data-testid="register-password-input"
                                />
                            </div>

                            <div className="form-group">
                                <Label htmlFor="confirmPassword" className="form-label">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your password"
                                    required
                                    className="swiss-input"
                                    data-testid="register-confirm-password-input"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full swiss-btn-primary"
                                data-testid="register-submit-btn"
                            >
                                {loading ? 'Creating account...' : 'Create Account'}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-zinc-500">
                                Already have an account?{' '}
                                <Link 
                                    to="/login" 
                                    className="text-orange-600 font-semibold hover:text-orange-700 transition-colors"
                                    data-testid="login-link"
                                >
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
